<template>
  <div>
    <div v-if="!isLoading && addonNeeded">
      <locked
        title="Individual Participants data"
        secondary-title="Per participant, you’ll get:"
        addon-name="Individual Participants data Addon"
        description="Purchase this add-on to access more data in your report."
        :info-items="[
          'Emotion recording & eye tracking video',
          'Demographics',
          'Associations',
          'Evaluation scores and additional comments',
          'Open questions',
        ]"
        price="900,-"
        :show-icon="false"
      ></locked>
    </div>
    <div v-if="!isLoading && testState.id && dropdownValue && !addonNeeded">
      <router-link
        class="link-back"
        :to="`/video-test/${$route.params.id}/participant`"
      >
        <div>&larr; Back to participants list</div>
      </router-link>
      <base-page-title
        v-if="data"
        :style="{ marginTop: '20px' }"
        :title="`Participant ${data.id}`"
      ></base-page-title>
      <div
        class="header-stats"
        v-if="!isLoading && testState.id && dropdownValue"
      >
        <div class="stat-col stat-col--nav">
          <bg-select
            @change="handleSelectChange"
            :options="participants"
            :value="dropdownValue"
            class="w60 mb0"
          ></bg-select>
          <div class="navButtons">
            <bg-navigation :link="backButtonLink" icon="chevronLeft" />
            <bg-navigation :link="nextButtonLink" icon="chevronRight" />
          </div>
        </div>
        <div class="stat-col stat-col--fav">
          <favourite
            entity-type="participant"
            :entity-data="{
              testId: testState.id,
              participantId: data.id,
            }"
            :active="data.favourite"
          ></favourite>
        </div>
        <div class="stat-col" v-if="data.total_clicks">
          <div class="stat-header">Clicks</div>
          <div class="stat-value">{{ data.total_clicks }}</div>
        </div>
        <div class="stat-col">
          <div class="stat-header">Age</div>
          <div class="stat-value">{{ this.data.demographic.age }}</div>
        </div>
        <div class="stat-col">
          <div class="stat-header">Education</div>
          <div class="stat-value stat-value--upper">
            {{ data.demographic.education }}
          </div>
        </div>
        <div class="stat-col">
          <div class="stat-header">Gender</div>
          <div class="stat-value">{{ data.demographic.gender }}</div>
        </div>
      </div>
      <bg-video-chart class="vid-chart" v-if="!isLoading" :data="data" />
      <div v-if="!isLoading" class="q-container">
        <div class="question-container">
          <base-page-section-title
            bold-title="Associations: "
            :normal-title="`Participant ${this.data.id}`"
          ></base-page-section-title>
          <div class="question-answer">
            <div class="q-content">
              <div class="q-q">Question</div>
              Choose max 5 associations you think match with the video you have
              just watched
            </div>
            <div class="q-content">
              <div class="q-q">Answer</div>
              <div class="q-list">
                <div v-for="a in chosenAssociations" v-bind:key="a.id">
                  &bull; {{ a.text }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="question-container">
          <base-page-section-title
            bold-title="Questions on a scale from 1 to 5: "
            :normal-title="`Participant ${this.data.id}`"
          ></base-page-section-title>
          <bg-select
            :options="scaleOptions"
            @change="scaleChange"
            :value="scaleValue"
          ></bg-select>
          <div class="question-answer" v-if="scaleValue">
            <div class="q-content">
              <div class="q-q">Question</div>
              {{ scaleValue.question }}
            </div>
            <div class="q-content">
              <div class="q-q">Given score</div>
              {{ currentScaleAnswer.score }}
            </div>
            <div class="q-content" v-if="currentScaleAnswer.answer">
              <div class="q-q">Comment</div>
              {{ currentScaleAnswer.answer }}
            </div>
          </div>
        </div>
        <div class="question-container">
          <base-page-section-title
            bold-title="Open questions: "
            :normal-title="`Participant ${this.data.id}`"
          ></base-page-section-title>
          <bg-select
            :options="openOptions"
            @change="openChange"
            :value="openValue"
            :extra-wide="true"
          ></bg-select>
          <div class="question-answer" v-if="openValue">
            <div class="q-content">
              <div class="q-q">Question</div>
              {{ openValue.label }}
            </div>
            <div class="q-content" v-if="currentOpenAnswer">
              <div class="q-q">Answer</div>
              {{ currentOpenAnswer }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.header-stats {
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
}

.stat-col {
  margin-right: 50px;
  display: flex;
  flex-direction: column;
}

.vid-chart {
  margin-bottom: 100px;
}

.stat-header {
  font-weight: 300;
}

.stat-value {
  font-weight: 700;
  font-size: 24px;
  color: var(--bg-color-bg-dark);
}

.stat-value--upper {
  text-transform: uppercase;
}

.stat-col--nav {
  flex-direction: row;
  margin-right: 5px;
}

.stat-col--fav {
  margin-right: 170px;
}

.navButtons {
  display: flex;
}

.navButtons a {
  margin-right: 10px;
}

.navButtons a:first-child {
  margin-left: 15px;
}

.question-answer {
  flex-direction: column;
  display: flex;
  background-color: white;
  padding: 20px 40px;
  border-radius: var(--bg-border-radius);
  cursor: pointer;
  border: 1px solid white;
  border-radius: var(--bg-border-radius);
}

.q-q {
  font-weight: 300;
  color: var(--bg-color-gray-dark);
  font-size: 12px;
  margin-bottom: 8px;
}
.q-content {
  margin-bottom: 16px;
}
.q-container {
  margin-bottom: 100px;
}

.w60 {
  width: 60%;
}
.mb0 {
  margin-bottom: 0px;
}

.question-container {
  margin-top: 40px;
}

.link-back {
  text-decoration: underline;
  color: var(--bg-color-bg-dark);
  &:visited {
    color: var(--bg-color-bg-dark);
  }
  & > div {
    display: contents;
  }
}
</style>

<script>
import BgTypography from "@/directives/bg-typography";
import BgSelect from "@/components/bg-select";
import BasePageTitle from "@/components/base-page-title";
import BgNavigation from "@/components/bg-navigation";
import API from "@/api";
import BgVideoChart from "@/components/bg-video-chart";
import BgLoader from "@/components/bg-loader";
import Favourite from "@/components/favourite";
import moment from "moment";
import BasePageSectionTitle from "@/components/base-page-section-title";
import { mapState } from "vuex";
import CustomTemplate from "@/mixins/CustomTemplate";
import Locked from "@/components/base-locked";

export default {
  mixins: [CustomTemplate],
  name: "participant",
  components: {
    BgVideoChart,
    BgLoader,
    BgSelect,
    BgNavigation,
    BasePageSectionTitle,
    Locked,
    BasePageTitle,
    Favourite,
  },
  directives: {
    BgTypography,
  },
  data() {
    return {
      isLoading: true,
      data: null,
      participants: [],
      dropdownValue: false,
      scaleOptions: [],
      scaleValue: null,
      openOptions: [],
      openValue: [],
      templateData: {},
      chosenAssociations: [],
      addonNeeded: false,
    };
  },
  mounted() {
    this.getParticipants();
    this.templateData = {
      brand_name: this.testState.brand_name,
    };
    API.test.getTest(this.$route.params.id).then((res) => {
      if (!res.addons.includes("individual_participant")) {
        this.addonNeeded = true;
      }
      this.participants = res.participants
        .filter((p) => p.type === "default")
        .map((p) => {
          return {
            value: p.id,
            label: `Participant ${p.id}`,
          };
        });
      this.dropdownValue = {
        value: this.$route.params.participantId,
        label: `Participant ${this.$route.params.participantId}`,
      };
    });
  },
  methods: {
    scaleChange(newValue) {
      this.scaleValue = newValue;
    },
    openChange(newValue) {
      this.openValue = newValue;
    },
    getParticipants() {
      API.test
        .getParticipant(this.$route.params.id, this.$route.params.participantId)
        .then((res) => {
          this.isLoading = false;
          this.data = res;
          this.scaleOptions = res.evaluations.map((p) => {
            return {
              value: p.id,
              label: p.text,
              question: p.description,
            };
          });
          this.scaleValue = {
            value: res.evaluations[0].id,
            label: res.evaluations[0].text,
            question: res.evaluations[0].description,
          };
          this.openOptions = res.open_questions.map((p) => {
            return {
              value: p.id,
              label: p.text,
            };
          });
          this.openValue = {
            value: res.open_questions[0].id,
            label: res.open_questions[0].text,
          };
          this.chosenAssociations = res.associations.filter(
            (a) => a.answers.length > 0
          );
        });
    },
    handleSelectChange(val) {
      if (this.$route.participantId !== val) {
        this.$router.replace(
          `/video-test/${this.$route.params.id}/participant/${val.value}`
        );
      }
    },
  },
  watch: {
    $route(to, from) {
      if (to.params.participantId !== from.params.participantId) {
        this.isLoading = true;
        this.getTest();
      }
    },
  },
  computed: {
    currentScaleAnswer() {
      const currentQ = this.data.evaluations.find(
        (e) => e.id === this.scaleValue.value
      );
      return {
        answer: currentQ.answers[0] ? currentQ.answers[0].text : "",
        score: currentQ.answers[0] ? currentQ.answers[0].score : 0,
      };
    },
    currentOpenAnswer() {
      const currentQ = this.data.open_questions.find(
        (e) => e.id === this.openValue.value
      );
      return currentQ.answers[0] ? currentQ.answers[0].text : "";
    },
    timeToComplete(data) {
      const startTime = moment(this.data.started_at);
      const endTime = moment(this.data.ended_at);
      const diff = endTime.diff(startTime);
      return moment(diff).format("mm:ss");
    },
    nextButtonLink() {
      const newIndex =
        this.$route.params.participantId >= this.participants.length
          ? 1
          : Number(this.$route.params.participantId) + 1;

      return `/video-test/${this.testState.id}/participant/${newIndex}`;
    },
    backButtonLink() {
      const newIndex =
        this.$route.params.participantId == 1
          ? this.participants.length
          : Number(this.$route.params.participantId) - 1;

      return `/video-test/${this.testState.id}/participant/${newIndex}`;
    },
    ...mapState({
      testState: (state) => state.test,
    }),
  },
};
</script>
